import { FileData } from "../context/DataContext";
import { MaxDrawdown, AvgWorst5DD, MaxRunup, TradingPeriod, OverallTradingMetrics, YearlyTradingMetrics, MonthlyTradingMetrics, DailyTradingMetrics, WeeklyTradingMetrics } from "../interfaces/Metrics";
import { TradingData } from "./CSVParser";

class TradingMetricsCalculator {
  private tradingData: TradingData[];
  private initialCapital = 0;
  private neverCalculateInitialCapital = false;

  constructor(tradingData: TradingData[], recalculate: boolean = false, initialCapital: number = 0) {
    this.tradingData = tradingData;
    if (initialCapital !== 0) {
      this.initialCapital = initialCapital;
      this.neverCalculateInitialCapital = true;
      console.log("custom capital: " + initialCapital);
    }
    if (recalculate) {
      this.tradingData = TradingMetricsCalculator.recalculateTradeList(tradingData, initialCapital);
    } 
  }

  // All Entries
  getAllEntries(tradingData?: TradingData[]): TradingData[] {
    const source = tradingData ? tradingData : this.tradingData;
    return source.filter((data) =>
      data.type.toLocaleLowerCase().includes("entry")
    );
  }

  // All Entries
  getAllExits(tradingData?: TradingData[]): TradingData[] {
    const source = tradingData ? tradingData : this.tradingData;
    return source.filter((data) =>
      data.type.toLocaleLowerCase().includes("exit")
    );
  }

  // Total Net Profit
  getTotalNetProfit(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits.reduce((total, data) => total + data.profit, 0);
  }

  // Gross Profit
  getGrossProfit(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits
      .filter((data) => data.profit > 0)
      .reduce((total, data) => total + data.profit, 0);
  }

  // Gross Loss
  getGrossLoss(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits
      .filter((data) => data.profit < 0)
      .reduce((total, data) => total + data.profit, 0);
  }

  // Profit Factor
  getProfitFactor(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const grossProfit = this.getGrossProfit(exits);
    const grossLoss = Math.abs(this.getGrossLoss(exits));
    return grossProfit / grossLoss;
  }

  // Max Drawdown
  getMaxDrawdown(tradingData?: TradingData[]): MaxDrawdown {
    const exits = this.getAllExits(tradingData);
    let maxDrawdown = 0;
    let maxDrawdownDate = 0;
    let asPercentOfInitialEquity = 0;
    let asPercentOfTotalEquity = 0;
    let actualCumulativeDrawdown = 0;
    let lastEntryWasRelevant = false;
    let startIndex = 0;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit < 0 && !lastEntryWasRelevant) {
        actualCumulativeDrawdown += exits[i].profit;
        startIndex = i; // remember when we start a drawdown again
        if (actualCumulativeDrawdown < maxDrawdown) {
          maxDrawdown = actualCumulativeDrawdown;
          maxDrawdownDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxDrawdown / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxDrawdown / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
      } else if (exits[i].profit < 0 && lastEntryWasRelevant) {
        actualCumulativeDrawdown += exits[i].profit;
        if (actualCumulativeDrawdown < maxDrawdown) {
          maxDrawdown = actualCumulativeDrawdown;
          maxDrawdownDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxDrawdown / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxDrawdown / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
      } else {
        lastEntryWasRelevant = false;
        actualCumulativeDrawdown = 0;
      }
    }
    return { maxDrawdown, maxDrawdownDate, asPercentOfInitialEquity, asPercentOfTotalEquity };
  }

  // Longest Drawdown
  getLongestDrawdownDays(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    let maxDrawdownLength = 0;
    let lastEntryWasRelevant = false;
    let firstDate = 0;
    let lastDate = 0;
    let firstEntryFound;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit < 0 && (!lastEntryWasRelevant || !firstEntryFound)) {
        firstDate = exits[i].datetime.getTime();
        lastDate = exits[i].datetime.getTime();
        lastEntryWasRelevant = true;
        firstEntryFound = true;
      } else if (exits[i].profit < 0 && lastEntryWasRelevant) {
        lastDate = exits[i].datetime.getTime();
        if (lastDate - firstDate > maxDrawdownLength) {
          maxDrawdownLength = lastDate - firstDate;
        }
        lastEntryWasRelevant = true;
      } else {
        firstDate = 0;
        lastDate = 0;
        lastEntryWasRelevant = false;
      }
    }
    return maxDrawdownLength / (1000 * 60 * 60 * 24);
  }

  // Longest Runup in days
  getLongestRunupDays(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    let maxRunupLength = 0;
    let lastEntryWasRelevant = false;
    let firstDate = 0;
    let lastDate = 0;
    let firstEntryFound;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit > 0 && (!lastEntryWasRelevant || !firstEntryFound)) {
        firstDate = exits[i].datetime.getTime();
        lastDate = exits[i].datetime.getTime();
        lastEntryWasRelevant = true;
        firstEntryFound = true;
      } else if (exits[i].profit > 0 && lastEntryWasRelevant) {
        lastDate = exits[i].datetime.getTime();
        if ((lastDate - firstDate) > maxRunupLength) {
          maxRunupLength = lastDate - firstDate;
        }
        lastEntryWasRelevant = true;
      } else {
        firstDate = 0;
        lastDate = 0;
        lastEntryWasRelevant = false;
      }
    }
    return maxRunupLength / (1000 * 60 * 60 * 24);
  }

  // Longest Flat Period in days
  getLongestFlatPeriodDays(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    if (exits.length === 0) return 0;

    let maxFlatPeriodLength = 0;
    let currentFlatPeriodLength = 0;
    let previousDate = exits[0].datetime;

    for (let i = 1; i < exits.length; i++) {
      const currentDate = exits[i].datetime;
      const daysDifference = (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysDifference > 1) {
        currentFlatPeriodLength = daysDifference - 1;
        if (currentFlatPeriodLength > maxFlatPeriodLength) {
          maxFlatPeriodLength = currentFlatPeriodLength;
        }
      }
      previousDate = currentDate;
    }

    return maxFlatPeriodLength;
  }

  // Pessimistic Risk/Reward Ratio
  getPessimisticRR(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxDrawdown = this.getMaxDrawdown(exits).maxDrawdown;
    const totalTrades = this.getTotalTrades(exits);
    const stdDevOfGains = this.getStandardDeviationOfGains(exits);

    const uncertaintyDiscount = totalTrades * stdDevOfGains;
    const pessimisticNetProfit = totalNetProfit - uncertaintyDiscount;

    return pessimisticNetProfit / Math.abs(maxDrawdown);
  }

  // Standard Deviation of Gains
  private getStandardDeviationOfGains(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const gains = this.getAllExits(exits).map(data => data.profit);
    const mean = gains.reduce((sum, value) => sum + value, 0) / gains.length;
    return Math.sqrt(gains.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / gains.length);
  }

  // Average Trade Net Profit
  getAvgTradeNetProfit(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const totalTrades = this.getAllExits(exits).length;
    return totalNetProfit / totalTrades;
  }

  getAvgTradeNetProfitPerMonth(tradingData?: TradingData[]): number {
    // get first an last date from tradingData and calculate the difference in months
    const exits = this.getAllExits(tradingData);
    const firstDate = exits[0].datetime;
    const lastDate = exits[exits.length - 1].datetime;
    // const months = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth()) + 1;
    const months = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
    return this.getTotalNetProfit(exits) / months;
  }

  // Average Winning Trade
  getAvgWinningTrade(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const winningTrades = exits.filter((data) => data.profit > 0);
    const totalWinningProfit = winningTrades.reduce(
      (total, data) => total + data.profit,
      0
    );
    return totalWinningProfit / winningTrades.length;
  }

  // Average Losing Trade
  getAvgLosingTrade(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const losingTrades = exits.filter((data) => data.profit < 0);
    const totalLosingProfit = losingTrades.reduce(
      (total, data) => total + data.profit,
      0
    );
    return totalLosingProfit / losingTrades.length;
  }

  // Winning Trades Count
  getWinningTradesCount(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits.filter((data) => data.profit > 0).length;
  }

  // Losing Trades Count
  getLosingTradesCount(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits.filter((data) => data.profit < 0).length;
  }

  // Total Trades
  getTotalTrades(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return exits.length;
  }

  // Percent Profitable
  getPercentProfitable(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalTrades = this.getTotalTrades(exits);
    const winningTrades = this.getWinningTradesCount(exits);
    return (winningTrades / totalTrades) * 100;
  }

  // Largest Win
  getLargestWin(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const largestWin = Math.max(
      ...this.getAllExits(exits).map((data) => data.profit)
    );
    return largestWin;
  }

  // Largest Loss
  getLargestLoss(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const largestLoss = Math.min(
      ...this.getAllExits(exits).map((data) => data.profit)
    );
    return largestLoss;
  }

  // Max Consecutive Winners
  getMaxConsWinners(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    let maxConsWinners = 0;
    let currentConsWinners = 0;
    let lastEntryWasWin = false;
    let firstEntryFound = false;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit > 0 && (!lastEntryWasWin || !firstEntryFound)) {
        currentConsWinners = 1;
        if (currentConsWinners > maxConsWinners) {
          maxConsWinners = currentConsWinners;
        }
        lastEntryWasWin = true;
        firstEntryFound = true;
      } else if (exits[i].profit > 0 && lastEntryWasWin) {
        currentConsWinners++;
        if (currentConsWinners > maxConsWinners) {
          maxConsWinners = currentConsWinners;
        }
        lastEntryWasWin = true;
      } else {
        lastEntryWasWin = false;
        currentConsWinners = 0;
      }
    }
    return maxConsWinners;
  }

  // Max Consecutive Losers
  getMaxConsLosers(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    let maxConsLosers = 0;
    let currentConsLosers = 0;
    let lastEntryWasRelevant = false;
    let firstEntryFound = false;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit < 0 && (!lastEntryWasRelevant || !firstEntryFound)) {
        currentConsLosers = 1;
        if (currentConsLosers > maxConsLosers) {
          maxConsLosers = currentConsLosers;
        }
        lastEntryWasRelevant = true;
        firstEntryFound = true;
      } else if (exits[i].profit < 0 && lastEntryWasRelevant) {
        currentConsLosers++;
        if (currentConsLosers > maxConsLosers) {
          maxConsLosers = currentConsLosers;
        }
        lastEntryWasRelevant = true;
      } else {
        lastEntryWasRelevant = false;
        currentConsLosers = 0;
      }
    }
    return maxConsLosers;
  }

  // Annual Rate of Return
  getAnnualRateOfReturn(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    // return this.getAvgTradeNetProfitPerMonth(exits);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const initialCapital = this.getInitialCapital(exits);
    return (totalNetProfit / initialCapital) * 100;
  }

  // Sharpe Ratio
  getSharpeRatio(riskFreeRate: number = 2, tradingData?: TradingData[]): number {
    try {
      const exits = this.getAllExits(tradingData);
      // const avgReturn = this.getAvgTradeNetProfit(exits);
      const avgReturn = this.getAvgTradeNetProfitPerMonth(exits);
      const stdDev = this.getStandardDeviationOfReturn(exits);
      return (avgReturn - (this.getInitialCapital() * riskFreeRate / 100)) / stdDev;
    } catch (error) {
      return -9999;
    }

  }

  // Standard Deviation of Monthly Return
  private getStandardDeviationOfReturn(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const monthlyReturns = this.getMonthlyReturns(exits);
    const mean = this.getAvgTradeNetProfitPerMonth(tradingData);
    const firstDate = exits[0].datetime;
    const lastDate = exits[exits.length - 1].datetime;
    const months = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
    const variance = monthlyReturns.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / months;
    return Math.sqrt(variance);
  }

  // Get monthly returns
  private getMonthlyReturns(tradingData?: TradingData[]): number[] {
    const exits = this.getAllExits(tradingData);
    const monthlyReturns: number[] = [];
    let cumulativeProfit = 0;
    let previousMonth = -1;

    this.getAllExits(exits).forEach((data) => {
      const currentMonth = data.datetime.getMonth();
      if (currentMonth !== previousMonth) {
        if (previousMonth !== -1) {
          monthlyReturns.push(cumulativeProfit);
        }
        cumulativeProfit = 0;
      }
      cumulativeProfit += data.profit;
      previousMonth = currentMonth;
    });
    if (previousMonth !== -1) {
      monthlyReturns.push(cumulativeProfit);
    }
    return monthlyReturns;
  }

  // Margin Requirements
  getMarginRequirements(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    return Math.max(...this.getAllExits(exits).map((data) => data.contracts));
  }

  // Max Intraday Drawdown
  getMaxIntradayDrawdown(tradingData?: TradingData[]): MaxDrawdown {
    const exits = this.getAllExits(tradingData);
    let maxDrawdown = 0;
    let maxDrawdownDate = 0;
    let asPercentOfInitialEquity = 0;
    let asPercentOfTotalEquity = 0;
    let actualCumulativeDrawdown = 0;
    let lastEntryWasRelevant = false;
    let startIndex = 0;
    let previousDayStr = "";
    for (let i = 0; i < exits.length; i++) {
      // get current day to remember when we potentially start a drawdown again
      const currentDayStr = exits[i].datetime.toLocaleDateString();
      // if we have a loss and the last entry was not relevant, we start a new drawdown
      if (exits[i].profit < 0 && !lastEntryWasRelevant) {
        actualCumulativeDrawdown = exits[i].profit; // reset the drawdown
        startIndex = i; // remember when we started this drawdown for later
        if (actualCumulativeDrawdown < maxDrawdown) { // only if we are on the same day or the first entry
          maxDrawdown = actualCumulativeDrawdown;
          maxDrawdownDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxDrawdown / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxDrawdown / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
      } else if (exits[i].profit < 0 && lastEntryWasRelevant && currentDayStr === previousDayStr) { // if we have a loss on the same day and the last entry was also relevant, we add to the drawdown
        actualCumulativeDrawdown += exits[i].profit;
        if (actualCumulativeDrawdown < maxDrawdown) { // if we have a new max drawdown, we update the values
          maxDrawdown = actualCumulativeDrawdown;
          maxDrawdownDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxDrawdown / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxDrawdown / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
      } else {
        lastEntryWasRelevant = false;
        actualCumulativeDrawdown = 0;
      }
      previousDayStr = currentDayStr;
    }
    return { maxDrawdown, maxDrawdownDate, asPercentOfInitialEquity, asPercentOfTotalEquity };
  }

  // Recovery Factor
  getRecoveryFactor(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxDrawdown = this.getMaxDrawdown(exits);
    return totalNetProfit / Math.abs(maxDrawdown.maxDrawdown);
  }

  // NP/MaxDD
  getNPMaxDD(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxDrawdown = this.getMaxDrawdown(exits);
    return totalNetProfit / Math.abs(maxDrawdown.maxDrawdown);
  }

  // NP/Intraday MaxDD
  getNPIntradayMaxDD(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxIntradayDrawdown = this.getMaxIntradayDrawdown(exits);
    return totalNetProfit / Math.abs(maxIntradayDrawdown.maxDrawdown);
  }

  // Avg. Worst 5 DD
  getAvgWorst5DD(tradingData?: TradingData[]): AvgWorst5DD {
    const exits = this.getAllExits(tradingData);
    const drawdowns: number[] = [];
    exits.forEach((data) => {
      if (data.profit < 0) {
        drawdowns.push(data.profit);
      }
    });
    drawdowns.sort((a, b) => a - b);
    const worst5DD = drawdowns.slice(0, 5);
    const avg = worst5DD.reduce((sum, dd) => sum + dd, 0) / worst5DD.length;
    return { avgWorst5DD: avg, asPercentOfInitialEquity: (avg / this.initialCapital) * 100 };
  }

  // Max Runup
  getMaxRunup(tradingData?: TradingData[]): MaxRunup {
    const exits = this.getAllExits(tradingData);
    let maxRunup = 0;
    let maxRunupDate = 0;
    let asPercentOfInitialEquity = 0;
    let asPercentOfTotalEquity = 0;
    let cumulativeProfit = 0;
    let lastEntryWasRelevant = false;
    let startIndex = 0;
    let firstEntryFound = false;
    for (let i = 0; i < exits.length; i++) {
      if (exits[i].profit > 0 && (!lastEntryWasRelevant || !firstEntryFound)) {
        cumulativeProfit = exits[i].profit;
        startIndex = i; // remember when we start a runup again
        if (cumulativeProfit > maxRunup) {
          maxRunup = cumulativeProfit;
          maxRunupDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxRunup / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxRunup / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
        firstEntryFound = true;
      } else if (exits[i].profit > 0 && lastEntryWasRelevant) {
        cumulativeProfit += exits[i].profit;
        if (cumulativeProfit > maxRunup) {
          maxRunup = cumulativeProfit;
          maxRunupDate = exits[i].datetime.getTime();
          asPercentOfInitialEquity = (maxRunup / this.getInitialCapital(exits)) * 100;
          asPercentOfTotalEquity = (maxRunup / (this.getInitialCapital(exits) + exits[startIndex].cumulativeProfit)) * 100;
        }
        lastEntryWasRelevant = true;
      } else {
        lastEntryWasRelevant = false;
        cumulativeProfit = 0;
      }
    }
    return { maxRunup, maxRunupDate, asPercentOfInitialEquity, asPercentOfTotalEquity };
  }

  // Return on Initial Capital
  getReturnOnInitialCapital(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const initialCapital = this.getInitialCapital(exits);
    return (totalNetProfit / initialCapital) * 100;
  }

  // Avg. Monthly Return
  getAvgMonthlyReturn(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const monthlyReturns = this.getMonthlyReturns(exits);
    return (
      monthlyReturns.reduce((sum, value) => sum + value, 0) /
      monthlyReturns.length
    );
  }

  // Std. Deviation of Monthly Return
  getStdDevMonthlyReturn(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const monthlyReturns = this.getMonthlyReturns(exits);
    const mean =
      monthlyReturns.reduce((sum, value) => sum + value, 0) /
      monthlyReturns.length;
    return Math.sqrt(
      monthlyReturns.reduce(
        (sum, value) => sum + Math.pow(value - mean, 2),
        0
      ) / monthlyReturns.length
    );
  }

  // % Profitable Months
  getPercentProfitableMonths(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const monthlyReturns = this.getMonthlyReturns(exits);
    const profitableMonths = monthlyReturns.filter(
      (returnValue) => returnValue > 0
    ).length;
    return (profitableMonths / monthlyReturns.length) * 100;
  }

  // Sterling Ratio
  getSterlingRatio(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const avgWorst5DD = this.getAvgWorst5DD(exits);
    return totalNetProfit / Math.abs(avgWorst5DD.avgWorst5DD);
  }

  // MAR Ratio
  getMARRatio(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxDrawdown = this.getMaxDrawdown(exits);
    return totalNetProfit / Math.abs(maxDrawdown.maxDrawdown);
  }

  // Efficiency Factor
  getEfficiencyFactor(tradingData?: TradingData[]): number {
    const exits = this.getAllExits(tradingData);
    const totalNetProfit = this.getTotalNetProfit(exits);
    const maxRunup = this.getMaxRunup(exits);
    return totalNetProfit / maxRunup.maxRunup;
  }

  // The complete trading period
  getTradingPeriod(tradingData?: TradingData[]): TradingPeriod {
    const exits = this.getAllExits(tradingData);
    const result = {
      start: 0,
      end: 0,
      tradingDays: 0,
    };
    if (exits.length === 0) return result;
    const firstDate = exits[0].datetime.getTime();
    const lastDate = exits[exits.length - 1].datetime.getTime();
    const uniqueDays = new Set(exits.map(data => data.datetime.toLocaleDateString()));
    result.start = firstDate;
    result.end = lastDate;
    result.tradingDays = uniqueDays.size;
    return result;
  }

  // Calculate Initial Capital (works only for the first trade)
  getInitialCapital(tradingData?: TradingData[]): number {
    if (this.neverCalculateInitialCapital) return this.initialCapital;
    const exits = this.getAllExits(tradingData);
    return TradingMetricsCalculator.calculateInitialCapital(exits);
  }

  // Calculate Initial Capital (works only for the first trade)
  static calculateInitialCapital(tradingData: TradingData[]): number {
    const firstTrade = tradingData.filter((data) =>
      data.type.toLocaleLowerCase().includes("exit")
    )[0];
    if (firstTrade) {
      // return (firstTrade.profit * 100) / firstTrade.profitPercent;
      return (firstTrade.cumulativeProfit * 100) / firstTrade.cumulativeProfitPercent;
    }
    return 0;
  }

  calculateOverallMetrics(): OverallTradingMetrics {
    return {
      totalNetProfit: this.getTotalNetProfit(),
      grossProfit: this.getGrossProfit(),
      grossLoss: this.getGrossLoss(),
      profitFactor: this.getProfitFactor(),
      maxDrawdown: this.getMaxDrawdown(),
      pessimisticRR: this.getPessimisticRR(),
      avgTradeNetProfit: this.getAvgTradeNetProfit(),
      avgWinningTrade: this.getAvgWinningTrade(),
      avgLosingTrade: this.getAvgLosingTrade(),
      winningTradesCount: this.getWinningTradesCount(),
      losingTradesCount: this.getLosingTradesCount(),
      totalTrades: this.getTotalTrades(),
      percentProfitable: this.getPercentProfitable(),
      largestWin: this.getLargestWin(),
      largestLoss: this.getLargestLoss(),
      maxConsWinners: this.getMaxConsWinners(),
      maxConsLosers: this.getMaxConsLosers(),
      annualRateOfReturn: this.getAnnualRateOfReturn(),
      sharpeRatio: this.getSharpeRatio(),
      marginRequirements: this.getMarginRequirements(),
      maxIntradayDrawdown: this.getMaxIntradayDrawdown(),
      longestDrawdownDays: this.getLongestDrawdownDays(),
      recoveryFactor: this.getRecoveryFactor(),
      npMaxDD: this.getNPMaxDD(),
      npIntradayMaxDD: this.getNPIntradayMaxDD(),
      avgWorst5DD: this.getAvgWorst5DD(),
      maxRunup: this.getMaxRunup(),
      longestRunupDays: this.getLongestRunupDays(),
      longestFlatPeriodDays: this.getLongestFlatPeriodDays(),
      returnOnInitialCapital: this.getReturnOnInitialCapital(),
      avgMonthlyReturn: this.getAvgMonthlyReturn(),
      stdDevMonthlyReturn: this.getStdDevMonthlyReturn(),
      percentProfitableMonths: this.getPercentProfitableMonths(),
      tradingPeriod: this.getTradingPeriod(),
      initialCapital: this.getInitialCapital(),
    };
  }

  calculateYearlyMetrics(tradingData?: TradingData[]): YearlyTradingMetrics[] {
    let yearlyMetrics: YearlyTradingMetrics[] = [];

    for (let i = 0; i < this.getAllExits(tradingData).length; i++) {
      const trade = this.getAllExits(tradingData)[i];

      const year = new Date(trade.datetime).getFullYear();
      // add year if not existing yet
      if (!yearlyMetrics.find((metrics) => metrics.period === year)) {
        yearlyMetrics.push({
          period: year,
          netProfit: 0,
          returnOnIE: 0,
          returnOnTE: 0,
          trades: 0,
          profitFactor: 0,
          percentProfitable: 0,
        });
      }

      // update the correct entry now
      yearlyMetrics = yearlyMetrics.map((metrics) => {
        if (metrics.period === year) {
          metrics.netProfit += trade.profit;
          metrics.trades += 1;
        }
        return metrics;
      });
    }

    for (let i = 0; i <= yearlyMetrics.length; i++) {
      const metrics = yearlyMetrics[i];
      if (metrics) {
        const yearData = this.tradingData.filter(
          (data) => new Date(data.datetime).getFullYear() === metrics.period
        );
        metrics.returnOnIE =
          (metrics.netProfit / TradingMetricsCalculator.calculateInitialCapital(yearData)) *
          100;
        metrics.returnOnTE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(
              tradingData ? tradingData : this.tradingData
            )) * 100;
        metrics.profitFactor = this.getProfitFactor(yearData);
        metrics.percentProfitable = this.getPercentProfitable(yearData);
      }
    }

    return yearlyMetrics;
  }

  calculateMonthlyMetrics(tradingData?: TradingData[]): MonthlyTradingMetrics[] {
    let monthlyMetrics: MonthlyTradingMetrics[] = [];

    for (let i = 0; i < this.getAllExits(tradingData).length; i++) {
      const trade = this.getAllExits(tradingData)[i];
      const month = new Date(trade.datetime).toLocaleDateString('en', {
        year: "numeric",
        month: "long",
      });
      // add month if not existing yet
      if (!monthlyMetrics.find((metrics) => metrics.period === month)) {
        monthlyMetrics.push({
          period: month,
          netProfit: 0,
          netProfitIfPositive: 0,
          netProfitIfNegative: 0,
          returnOnIE: 0,
          returnOnTE: 0,
          trades: 0,
          profitFactor: 0,
          percentProfitable: 0,
          totalProfits: 0,
          totalLosses: 0,
        });
      }

      // update the correct entry now
      monthlyMetrics = monthlyMetrics.map((metrics) => {
        if (metrics.period === month) {
          metrics.netProfit += trade.profit;
          metrics.trades += 1;
          if (trade.profit > 0) {
            metrics.totalProfits += trade.profit;
          } else {
            metrics.totalLosses += trade.profit;
          }
        }
        return metrics;
      });
    }

    for (let i = 0; i <= monthlyMetrics.length; i++) {
      const metrics = monthlyMetrics[i];
      if (metrics) {
        const monthData = this.tradingData.filter(
          (data) =>
            new Date(data.datetime).toLocaleDateString("en", {
              year: "numeric",
              month: "long",
            }) === metrics.period
        );
        metrics.returnOnIE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(monthData)) *
          100;
        metrics.returnOnTE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(
              tradingData ? tradingData : this.tradingData
            )) * 100;
        metrics.profitFactor = this.getProfitFactor(monthData);
        metrics.percentProfitable = this.getPercentProfitable(monthData);
        metrics.netProfitIfPositive = metrics.netProfit > 0 ? metrics.netProfit : 0;
        metrics.netProfitIfNegative = metrics.netProfit < 0 ? metrics.netProfit : 0;
      }
    }

    return monthlyMetrics;
  }

  calculateDailyMetrics(tradingData?: TradingData[]): DailyTradingMetrics[] {
    let dailyMetrics: DailyTradingMetrics[] = [];

    for (let i = 0; i < this.getAllExits(tradingData).length; i++) {
      const trade = this.getAllExits(tradingData)[i];

      const day = new Date(trade.datetime).toLocaleDateString();
      // add day if not existing yet
      if (!dailyMetrics.find((metrics) => metrics.period === day)) {
        dailyMetrics.push({
          period: day,
          netProfit: 0,
          returnOnIE: 0,
          returnOnTE: 0,
          trades: 0,
          profitFactor: 0,
          percentProfitable: 0,
        });
      }

      // update the correct entry now
      dailyMetrics = dailyMetrics.map((metrics) => {
        if (metrics.period === day) {
          metrics.netProfit += trade.profit;
          metrics.trades += 1;
        }
        return metrics;
      });
    }

    for (let i = 0; i <= dailyMetrics.length; i++) {
      const metrics = dailyMetrics[i];
      if (metrics) {
        const dayData = this.tradingData.filter(
          (data) => new Date(data.datetime).toLocaleDateString() === metrics.period
        );
        metrics.returnOnIE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(dayData)) *
          100;
        metrics.returnOnTE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(
              tradingData ? tradingData : this.tradingData
            )) * 100;
        metrics.profitFactor = this.getProfitFactor(dayData);
        metrics.percentProfitable = this.getPercentProfitable(dayData);
      }
    }

    return dailyMetrics;
  }

  calculateWeeklyMetrics(tradingData?: TradingData[]): WeeklyTradingMetrics[] {
    let weeklyMetrics: WeeklyTradingMetrics[] = [];

    for (let i = 0; i < this.getAllExits(tradingData).length; i++) {
      const trade = this.getAllExits(tradingData)[i];

      const week = this.getWeekNumber(new Date(trade.datetime));
      // add week if not existing yet
      if (!weeklyMetrics.find((metrics) => metrics.period === week)) {
        weeklyMetrics.push({
          period: week,
          netProfit: 0,
          returnOnIE: 0,
          returnOnTE: 0,
          trades: 0,
          profitFactor: 0,
          percentProfitable: 0,
        });
      }

      // update the correct entry now
      weeklyMetrics = weeklyMetrics.map((metrics) => {
        if (metrics.period === week) {
          metrics.netProfit += trade.profit;
          metrics.trades += 1;
        }
        return metrics;
      });
    }

    for (let i = 0; i <= weeklyMetrics.length; i++) {
      const metrics = weeklyMetrics[i];
      if (metrics) {
        const weekData = this.tradingData.filter(
          (data) => this.getWeekNumber(new Date(data.datetime)) === metrics.period
        );
        metrics.returnOnIE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(weekData)) *
          100;
        metrics.returnOnTE =
          (metrics.netProfit /
            TradingMetricsCalculator.calculateInitialCapital(
              tradingData ? tradingData : this.tradingData
            )) * 100;
        metrics.profitFactor = this.getProfitFactor(weekData);
        metrics.percentProfitable = this.getPercentProfitable(weekData);
      }
    }

    return weeklyMetrics;
  }

  static joinAndRecalculateTradeLists(files: FileData[]): TradingData[] {
    let initialCapital = 0;
    let allTrades: TradingData[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.parsedData) {
        if (file.initialCapital > 0 ) {
          initialCapital += file.initialCapital;
        } else {
          initialCapital += TradingMetricsCalculator.calculateInitialCapital(file.parsedData);
        }
        file.parsedData.forEach((trade) =>
          allTrades.push({ ...trade, customId: file.customId })
        );
      }
    }
    return TradingMetricsCalculator.recalculateTradeList(allTrades, initialCapital);
  }

  static recalculateTradeList(tradingData: TradingData[], initialCapital: number): TradingData[] {
    // sort all trades by datetime
    tradingData.sort((a, b) => a.datetime.getTime() - b.datetime.getTime());
    for (let i = 0; i < tradingData.length; i++) {
      const trade = tradingData[i];
      if (i === 0) {
        if (trade.type.toLowerCase().includes("exit")) {
          trade.profitPercent = (trade.profit / trade.price * trade.contracts) * 100;
          trade.cumulativeProfit = trade.profit;
          trade.cumulativeProfitPercent = (trade.cumulativeProfit / initialCapital) * 100;
          trade.total = trade.profit + initialCapital;
        } else {
          trade.cumulativeProfit = 0;
          trade.profitPercent = 0;
          trade.cumulativeProfitPercent = 0;
          trade.profit = 0;
          trade.total = initialCapital;
        }
      } else {
        if (trade.type.toLowerCase().includes("exit")) {
          trade.cumulativeProfit = tradingData[i-1].cumulativeProfit + trade.profit;
          trade.total = tradingData[i-1].total + trade.profit;
          trade.profitPercent = (trade.profit / trade.price * trade.contracts) * 100;
          trade.cumulativeProfitPercent = (trade.cumulativeProfit / initialCapital) * 100;
        } else {
          trade.cumulativeProfit = tradingData[i - 1].cumulativeProfit;
          trade.profit = 0;
          trade.profitPercent = 0;
          trade.cumulativeProfitPercent = 0;
          trade.total = tradingData[i-1].total 
        }
      }
    }
    return tradingData;
  }

  private getWeekNumber(date: Date): string {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    return `${weekNumber} / ${date.getFullYear()}`;
  }
}

export default TradingMetricsCalculator;
